import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useToast, Box, Center, Switch, FormControl, FormLabel, Text, Link } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useIntl, FormattedMessage } from "react-intl";
import { RootState } from "redux/store";
import { isIpV4, isIpV6 } from "utils/validation/ipValidation";
import { GetRequestsLeft } from "api/GetRequestsLeft";
import CheckersInputBlock from "../CheckersInputBlock";
import IpCheckerTabs from "./IpCheckerTabs";
import IpCheckerFreeData from "./IpCheckerFreeData";
import { useIpQueryContext } from "context/IpQueryContext";
import { useApiCheckerSettings } from "hooks/api/use-api-checker-settings";
import {
  checkerSwitchFromMode,
  modeFromCheckerSwitch,
} from "utils/apiChecker/settings";
import { isUserApiKey } from "utils/auth/apiKeyStore";
import { FOCUS_RING, switchSx } from "utils/chackra/controlStyles";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";

const MODE_UNAVAILABLE_HINT_ID = "api-mode-switch-unavailable";

const IpCheckerBlock = () => {
  const userIp = useSelector((state: RootState) => state.userIp.userIp);
  const key = useSelector((state: RootState) => state.apiKey.apiKey);
  const appDefaultApiKey = process.env.REACT_APP_DEFAULT_API_KEY;
  const isUserLoggedIn = useSelector((state: RootState) => state.userData.isUserLoggedIn);
  const intl = useIntl();
  const toast = useToast();
  const { setQueryIp, queryIp } = useIpQueryContext();
  const [isKeyActive, setIsKeyActive] = useState<boolean>(false);
  const [, setRequestsLeft] = useState<number | null>(null);
  const [ipAddress, setIPAddress] = useState<string>(userIp);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isInputEmpty, setIsInputEmpty] = useState<boolean>(true);
  // The switch IS the api key's stored report mode (`get/update_api_checker_service`),
  // the same setting the cabinet API page and the bot edit. Checked = custom
  // check (per-provider view, `?mode=false`); mapping in utils/apiChecker/settings.
  const showModeSwitch = isUserLoggedIn && isUserApiKey(key);
  const {
    settings: checkerSettings,
    confirmed: confirmedSettings,
    loading: loadingMode,
    loadError: modeLoadError,
    pending: pendingSettings,
    update: updateSetting,
  } = useApiCheckerSettings(showModeSwitch ? key : null);
  // The switch shows the optimistic value; the paid lookup reads only the mode
  // the server CONFIRMED. Every new lookup mode is a real (chargeable)
  // `/check-ip`, so a save that fails and snaps back must never reach it.
  const modeEnabled = checkerSettings ? checkerSwitchFromMode(checkerSettings.mode) : true;
  const lookupModeEnabled = confirmedSettings
    ? checkerSwitchFromMode(confirmedSettings.mode)
    : true;
  // Settings that failed to load can't be changed from here: the switch is
  // disabled with a hint instead of answering every click with an error.
  const modeUnavailable = modeLoadError !== null;
  const updatingMode = loadingMode || Boolean(pendingSettings.mode);
  // The paid lookup waits for the stored mode: firing it with the default and
  // again once the mode loads would run (and charge) the check twice. A failed
  // load falls back to the default mapping once, never an endless wait.
  const modeResolved = !showModeSwitch || confirmedSettings !== null || modeUnavailable;

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const trimmedValue = value?.trim() || '';
    setIPAddress(trimmedValue);
    setIsValid(isIpV4(trimmedValue) || isIpV6(trimmedValue));
  }, []);

  const checkIpAddress = useCallback(() => {
    if (isValid && ipAddress) {
      setQueryIp(ipAddress.trim());
    } else {
      toast({
        position: "top",
        title: intl.formatMessage({ id: "IpValidationErrorMessage" }),
        status: "error",
        isClosable: true,
      });
    }
  }, [isValid, ipAddress, setQueryIp, toast, intl]);

  const resetValue = useCallback(() => setIPAddress(""), []);

  // Optimistic: the switch flips at once and snaps back with an error toast
  // when the save fails (never a success toast for a failed save).
  const handleModeToggle = useCallback(async (checked: boolean) => {
    if (!showModeSwitch || modeUnavailable) return;
    const saved = await updateSetting("mode", modeFromCheckerSwitch(checked));
    toast(
      saved
        ? {
            title: intl.formatMessage({ id: checked ? "CustomModeEnabled" : "AutomatedModeEnabled" }),
            description: intl.formatMessage({
              id: checked ? "CustomModeDescriptionToast" : "AutomatedModeDescriptionToast",
            }),
            status: "success",
            duration: 3000,
            isClosable: true,
          }
        : {
            title: intl.formatMessage({ id: "UpdateError" }),
            description: intl.formatMessage({ id: "UpdateErrorDescription" }),
            status: "error",
            duration: 5000,
            isClosable: true,
          }
    );
  }, [showModeSwitch, modeUnavailable, updateSetting, toast, intl]);

  useEffect(() => {
    setIsInputEmpty(!ipAddress?.trim());
  }, [ipAddress]);

  useEffect(() => {
    if (userIp) {
      setQueryIp(userIp);
      setIPAddress(userIp);
    }
  }, [userIp, setQueryIp]);

  useEffect(() => {
    const fetchData = async () => {
      if (process.env.NODE_ENV !== "development") {
        try {
          let hasRequests = false;

          // Check requests
          try {
            const requestsData = await GetRequestsLeft(key);
            if (requestsData?.data) {
              // API returns requests_left, not requests
              const requestsCount = requestsData.data.requests_left || requestsData.data.requests || 0;
              setRequestsLeft(requestsCount);
              hasRequests = requestsCount > 0;
            }
          } catch (error) {
            // console.error("Error fetching requests:", error);
          }

          // User has access to paid version if there are requests
          // (with or without subscription)
          setIsKeyActive(hasRequests && key !== appDefaultApiKey);
          
          // console.log("🔍 [IP_CHECKER] Access check:", {
          //   hasRequests,
          //   hasSubscription,
          //   isKeyActive: (hasRequests || hasSubscription) && key !== appDefaultApiKey,
          //   key: key !== appDefaultApiKey ? "custom" : "default"
          // });
        } catch (error) {
          // console.error("Error fetching user data:", error);
        }
      } else {
        // TEMPORARY FOR TESTING: force enable paid version
        setIsKeyActive(true);
        setRequestsLeft(10000000000);
        // console.log("🚀 [TEST_MODE] FORCING PAID VERSION ENABLED FOR TESTING mode=false");
      }
    };

    fetchData();
  }, [key, appDefaultApiKey]);

  const inputBlockProps = {
    heading: intl.formatMessage({
      id: "IpCheckerPageTitle",
    }),
    placeholder: userIp,
    buttonText: "IP",
    isInputEmpty: isInputEmpty,
    onClick: ipAddress !== queryIp ? checkIpAddress : () => {},
    onChange: handleChange,
    setIPAddress: resetValue,
    ipAddress: ipAddress,
  };

  // console.log("🚀 [COMPONENT_RENDER] Component rendering:", {
  //   isKeyActive,
  //   NODE_ENV: process.env.NODE_ENV,
  //   key: key !== appDefaultApiKey ? "custom" : "default",
  //   component: isKeyActive ? "🚀 IpCheckerTabs (PAID VERSION with mode=false)" : "🔒 IpCheckerFreeData (free version)"
  // });

  return (
    <main>
      <CheckersInputBlock {...inputBlockProps} />
      
      {/* Mode toggle for authorized users */}
      {showModeSwitch && (
        <Box
          mb={4}
          p={4}
          bg="bg.surface"
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.subtle"
          data-testid="api-mode-switch"
        >
          <FormControl
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexDirection={{ base: "column", md: "row" }}
            gap={{ base: 3, md: 4 }}
          >
            <Box textAlign={{ base: "center", md: "left" }}>
              <FormLabel htmlFor="api-mode-switch" mb={1} fontSize="sm" fontWeight="medium" color="text.primary">
                <FormattedMessage id="ApiModeSettings" />
              </FormLabel>
              <Text fontSize="sm" color="text.secondary">
                {modeEnabled
                  ? <FormattedMessage id="CustomModeDescription" />
                  : <FormattedMessage id="AutomatedModeDescription" />
                }{" "}
                <Link
                  as={RouterLink}
                  to="/profile/api"
                  color="brand.solid"
                  textDecoration="underline"
                  _focusVisible={FOCUS_RING}
                >
                  <FormattedMessage id="profileApi.manageLink" />
                </Link>
              </Text>
              {modeUnavailable && (
                <Text id={MODE_UNAVAILABLE_HINT_ID} fontSize="sm" color="text.secondary" mt={1}>
                  <FormattedMessage id="apiMode.loadError" />
                </Text>
              )}
            </Box>
            <Switch
              id="api-mode-switch"
              isChecked={modeEnabled}
              onChange={(e) => handleModeToggle(e.target.checked)}
              isDisabled={updatingMode || modeUnavailable}
              aria-describedby={modeUnavailable ? MODE_UNAVAILABLE_HINT_ID : undefined}
              size="lg"
              sx={switchSx}
            />
          </FormControl>
        </Box>
      )}

      {!isKeyActive ? (
        <IpCheckerFreeData />
      ) : modeResolved ? (
        <IpCheckerTabs modeEnabled={lookupModeEnabled} />
      ) : (
        <Center>
          <LoaderSpinner />
        </Center>
      )}
    </main>
  );
};

export default IpCheckerBlock;
